import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "title", "message", "action", "onClose", "kind", "show", "dismissible", "icon"];
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useEventCallback from '@restart/hooks/useEventCallback';
import Fade from '../Fade';
import { KINDS, SIZES, isFunction } from '../helper';
import { MessageContainer, MessageTitle, MessageIcon, MessageContent, MessageAction, MessageClose, MessageText } from '../MessageBase';
import { useDeprecated } from '../hooks';
const Root = styled(MessageContainer).withConfig({
  displayName: "Message__Root",
  componentId: "sc-ormo2i-0"
})(["position:relative;", "{margin:var(--size-spacing-01) 0 0 0;}"], MessageText.Style);
const Message = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    title,
    message,
    action,
    onClose,
    kind,
    show,
    dismissible,
    icon
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  const callbackOnClose = useEventCallback(event => isFunction(onClose) && onClose(event));
  const content = children || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MessageIcon, {
    kindMessage: kind,
    size: SIZES.MD,
    icon: icon
  }), /*#__PURE__*/React.createElement(MessageContent, {
    size: SIZES.MD
  }, /*#__PURE__*/React.createElement(MessageTitle, {
    size: SIZES.MD
  }, title), typeof message === 'string' ? /*#__PURE__*/React.createElement(MessageText, null, message) : !!message && message, /*#__PURE__*/React.createElement(MessageAction, null, action)));
  const messageContainerProps = Object.assign({
    size: SIZES.MD,
    kindMessage: kind,
    customizable: !!children
  }, rest);
  useDeprecated({
    action
  });
  return /*#__PURE__*/React.createElement(Fade, {
    in: show,
    keepMounted: false
  }, /*#__PURE__*/React.createElement(Root, _extends({
    ref: ref
  }, messageContainerProps, {
    "aria-live": "assertive",
    "aria-atomic": "true"
  }), content, dismissible && /*#__PURE__*/React.createElement(MessageClose, {
    size: SIZES.MD,
    onClick: callbackOnClose
  })));
});
Message.displayName = 'Message';
Message.kinds = {
  default: KINDS.DEFAULT,
  primary: KINDS.PRIMARY,
  success: KINDS.SUCCESS,
  danger: KINDS.DANGER,
  warning: KINDS.WARNING,
  help: KINDS.HELP
};
Message.Title = MessageTitle;
Message.Icon = MessageIcon;
Message.Content = MessageContent;
Message.Text = MessageText;
Message.defaultProps = {
  message: '',
  title: '',
  kind: Message.kinds.default,
  show: true,
  dismissible: true,
  children: null
};
process.env.NODE_ENV !== "production" ? Message.propTypes = {
  /**
   * Sets if content of Message will be customizable.
   *
   * @example
   * // Can be a string:
   * <Message title="Message title" message="Message content" />
   *
   * @example
   * // Or a node as Message.Text:
   * <Message
   *  title="Message title"
   *  message={<Message.Text>Message content</Message.Text>}
   * />
   */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    type: PropTypes.oneOf([Message.Text])
  })]),

  /**
   * Sets the title for the Message.
   */
  title: PropTypes.string,

  /**
   * Sets kind for styling.
   */
  kind: PropTypes.oneOf([Message.kinds.default, Message.kinds.primary, Message.kinds.success, Message.kinds.danger, Message.kinds.warning, Message.kinds.help]),

  /**
   * Sets when the Message will be shown or not.
   */
  show: PropTypes.bool,

  /**
   * Callback fired when the close button is clicked.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   */
  onClose: PropTypes.func,

  /**
   * Sets if the Message is dismissible making available a button that when clicked closes the Message.
   */
  dismissible: PropTypes.bool,

  /**
   * Sets the custom icon.
   */
  icon: PropTypes.element,

  /**
   * Sets the children.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.array, PropTypes.node]),

  /**
   * @deprecated
   *
   * Sets elements for action area.
   */
  action: PropTypes.node
} : void 0;
export default Message;